import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import ListMatchEntity from 'dashboard/components/ReportResult/ListMatchEntity';
import { MatchIconAlert, MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import {
  BusinessWatchlistMatch,
  BusinessWatchlistType,
} from 'dashboard/models/report/business-watchlist';
import { WatchlistMatch, WatchlistType } from 'dashboard/models/report/watchlist';
import Badge from 'design-system/components/Badge';
import Spacer from 'design-system/components/Spacer';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';

const AccordionTitle = styled.div`
  display: grid;
  grid-column-gap: ${Spacing[3]};
  grid-template-columns: ${Spacing[5]} 1fr;
  align-items: center;
  font-size: ${({ theme }) => theme.text.paragraph.lg.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.lg.lineHeight};
  font-weight: ${FontWeight.Medium};
`;

const AccordionContent = styled.div`
  margin-left: ${BASE * 4.5}px;
`;

const ListNameTitle = styled.div`
  display: flex;
  align-items: center;
`;

const MatchesBadge = styled(Badge)`
  margin-left: ${Spacing[2]};
`;

interface WatchlistAccordionsProps<
  MatchType extends WatchlistMatch | BusinessWatchlistMatch,
  ListNameType extends WatchlistType | BusinessWatchlistType,
> {
  groupedMatches?: { [K in ListNameType]?: MatchType[] };
  listOrder?: ListNameType[];
  title: (listName: ListNameType) => string;
  subtitle: (match: MatchType) => React.ReactNode;
  prefixKey?: string;
}

const WatchlistAccordions = <
  MatchType extends WatchlistMatch | BusinessWatchlistMatch,
  ListNameType extends WatchlistType | BusinessWatchlistType,
>({
  groupedMatches,
  listOrder,
  title,
  subtitle,
  prefixKey = '',
}: WatchlistAccordionsProps<MatchType, ListNameType>) => (
  <>
    {(listOrder ?? []).map((listName) => {
      const matches = groupedMatches?.[listName] ?? [];
      return (
        <Accordion
          key={`${prefixKey}-${listName}`}
          menuItem={true}
          padded={false}
          defaultExpanded={matches.length > 0}
          disabled={matches.length === 0}
          disabledTheme='primary'
          disabledHideCaret={true}
          menuItemHover={matches.length > 0}
          showCaret={matches.length > 0}
          titleContainerHeight='51px'
          title={
            <AccordionTitle>
              {matches.length === 0 ? <MatchIconPositive /> : <MatchIconAlert />}
              <ListNameTitle>
                {title(listName)}
                {matches.length > 0 && <MatchesBadge>{matches.length}</MatchesBadge>}
              </ListNameTitle>
            </AccordionTitle>
          }
        >
          <AccordionContent>
            <Spacer height={3} />
            {matches.map((match, index) => {
              return (
                <ListMatchEntity
                  key={`${match}-${index}`}
                  url={match.url}
                  name={match.name}
                  flagCode={match['flag-code'] || match['country-code']}
                >
                  {subtitle(match)}
                </ListMatchEntity>
              );
            })}
            <Spacer height={3} />
          </AccordionContent>
        </Accordion>
      );
    })}
  </>
);

export default WatchlistAccordions;
